/* eslint-disable @typescript-eslint/no-explicit-any */
import { GeneralError, GeneralErrorCause } from '51trips-frontend-core'
import { AxiosError } from 'axios'

// import translations from '../../application/assets/Translations'

const generalErrorConverter = {
  fromFirebaseAuthError: (
    error: any,
    contextDescription: string = 'Firebase Auth error',
    cause?: GeneralErrorCause
  ) => {
    const errorCode = error.code
    if (typeof errorCode === 'string') {
      switch (errorCode) {
        case 'auth/network-request-failed':
          return new GeneralError(contextDescription, GeneralErrorCause.networkConnection, error)
        case 'auth/too-many-requests':
          return new GeneralError(contextDescription, GeneralErrorCause.forbidden, error)
        case 'auth/invalid-email':
          return new GeneralError(contextDescription, GeneralErrorCause.invalidEmail, error)
        case 'auth/email-already-in-use':
          return new GeneralError(contextDescription, GeneralErrorCause.emailAlreadyInUse, error)
        case 'auth/weak-password':
          return new GeneralError(contextDescription, GeneralErrorCause.weakPassword, error)
        case 'auth/wrong-password':
          return new GeneralError(contextDescription, GeneralErrorCause.wrongPassword, error)
        case 'auth/user-not-found':
          return new GeneralError(contextDescription, GeneralErrorCause.userNotFound, error)
        /// What Firebase returns instead of `wrong-password` / `user-not-found`
        /// once email enumeration protection is on, which is the default for
        /// projects created from late 2023 on.
        case 'auth/invalid-credential':
        case 'auth/invalid-login-credentials':
          return new GeneralError(contextDescription, GeneralErrorCause.invalidCredentials, error)
        /// Reachable from `AuthenticationRepository.linkEmailPassword` when the
        /// sign-in that preceded it is no longer recent enough.
        case 'auth/requires-recent-login':
          return new GeneralError(contextDescription, GeneralErrorCause.unauthenticated, error)
        case 'auth/credential-already-in-use':
          return new GeneralError(contextDescription, GeneralErrorCause.emailAlreadyInUse, error)
        default:
          return new GeneralError(contextDescription, cause, error)
      }
    } else {
      return new GeneralError(contextDescription, cause, error)
    }
  },

  fromFirebaseFirestoreError: (
    error: any,
    contextDescription: string = 'Firebase Firestore error',
    cause?: GeneralErrorCause
  ) => {
    const errorCode = error.code
    if (typeof errorCode === 'string') {
      /// Firebase JS v9+ reports bare codes such as `unavailable`. Older SDKs
      /// prefixed them with `firestore/`.
      switch (errorCode.replace(/^firestore\//, '')) {
        case 'cancelled':
          return new GeneralError(contextDescription, GeneralErrorCause.requestCancelled, error)
        case 'unknown':
          return new GeneralError(contextDescription, GeneralErrorCause.unknown, error)
        case 'invalid-argument':
          return new GeneralError(contextDescription, GeneralErrorCause.invalidArgument, error)
        case 'deadline-exceeded':
          return new GeneralError(contextDescription, GeneralErrorCause.deadlineExceeded, error)
        case 'not-found':
          return new GeneralError(contextDescription, GeneralErrorCause.dataNotFound, error)
        case 'already-exists':
          return new GeneralError(contextDescription, GeneralErrorCause.alreadyExists, error)
        case 'permission-denied':
          return new GeneralError(contextDescription, GeneralErrorCause.permissionDenied, error)
        case 'resource-exhausted':
          return new GeneralError(contextDescription, GeneralErrorCause.resourceExhausted, error)
        case 'failed-precondition':
          return new GeneralError(contextDescription, GeneralErrorCause.failedPrecondition, error)
        case 'aborted':
          return new GeneralError(contextDescription, GeneralErrorCause.aborted, error)
        case 'out-of-range':
          return new GeneralError(contextDescription, GeneralErrorCause.outOfRange, error)
        case 'unimplemented':
          return new GeneralError(contextDescription, GeneralErrorCause.unimplemented, error)
        case 'internal':
          return new GeneralError(contextDescription, GeneralErrorCause.internalServerError, error)
        case 'unavailable':
          return new GeneralError(contextDescription, GeneralErrorCause.unavailable, error)
        case 'data-loss':
          return new GeneralError(contextDescription, GeneralErrorCause.dataLoss, error)
        case 'unauthenticated':
          return new GeneralError(contextDescription, GeneralErrorCause.forbidden, error)
        default:
          return new GeneralError(contextDescription, cause, error)
      }
    } else {
      return new GeneralError(contextDescription, cause, error)
    }
  },

  /**
   * Note that the numeric codes below come from the native Google Sign-In SDKs
   * and never fire on the web: `signInWithPopup` reports `auth/*` codes instead,
   * which is why those are handled first.
   */
  fromGoogleSignInError: (
    error: any,
    contextDescription: string = 'Firebase Auth error',
    cause?: GeneralErrorCause
  ) => {
    const errorCode = error.code
    if (typeof errorCode === 'string') {
      switch (errorCode) {
        /// Web (`signInWithPopup`)
        case 'auth/popup-closed-by-user':
        case 'auth/cancelled-popup-request':
        case 'auth/user-cancelled':
          return new GeneralError(
            contextDescription,
            GeneralErrorCause.googleRequestCancelled,
            error
          )
        case 'auth/popup-blocked':
        case 'auth/web-storage-unsupported':
          return new GeneralError(
            contextDescription,
            GeneralErrorCause.googleLogInNotPossible,
            error
          )
        /// The account already exists under another sign-in method. Only thrown
        /// when that account's email is verified — otherwise Firebase links
        /// Google to it and drops its password (see
        /// `AuthenticationRepository.isMissingPasswordSignIn`).
        case 'auth/account-exists-with-different-credential':
          return new GeneralError(contextDescription, GeneralErrorCause.emailAlreadyInUse, error)
        case 'auth/network-request-failed':
          return new GeneralError(contextDescription, GeneralErrorCause.networkConnection, error)
        case 'auth/too-many-requests':
          return new GeneralError(contextDescription, GeneralErrorCause.forbidden, error)
        case 'auth/internal-error':
          return new GeneralError(contextDescription, GeneralErrorCause.internalServerError, error)
        /// Native (Android / iOS Google Sign-In SDKs)
        case '12500':
        case '-1':
          return new GeneralError(
            contextDescription,
            GeneralErrorCause.googleLogInNotPossible,
            error
          )
        case '-2':
        case '-6':
          return new GeneralError(contextDescription, undefined, error)
        case '-4':
          return new GeneralError(
            contextDescription,
            GeneralErrorCause.googleInvalidCredential,
            error
          )
        case '12502':
          return new GeneralError(
            contextDescription,
            GeneralErrorCause.googleLogInPendingCompletion,
            error
          )
        case '12501':
        case '-5':
          return new GeneralError(
            contextDescription,
            GeneralErrorCause.googleRequestCancelled,
            error
          )
        case '-7':
          return new GeneralError(
            contextDescription,
            GeneralErrorCause.googleUserNotLoggedIn,
            error
          )
        case '-8':
          return new GeneralError(
            contextDescription,
            GeneralErrorCause.googleUserAlreadyLoggedIn,
            error
          )
        default:
          return new GeneralError(contextDescription, cause, error)
      }
    } else {
      return new GeneralError(contextDescription, cause, error)
    }
  },

  /**
   * As with Google, the numeric codes are native `ASAuthorization` ones and are
   * unreachable on the web, where Apple sign-in also goes through
   * `signInWithPopup` and reports `auth/*` codes.
   */
  fromAppleSignInError: (
    error: any,
    contextDescription: string = 'Apple Auth error',
    cause?: GeneralErrorCause
  ) => {
    const errorCode = error.code
    if (typeof errorCode === 'string') {
      switch (errorCode) {
        /// Web (`signInWithPopup`)
        case 'auth/popup-closed-by-user':
        case 'auth/cancelled-popup-request':
        case 'auth/user-cancelled':
          return new GeneralError(contextDescription, GeneralErrorCause.requestCancelled, error)
        case 'auth/popup-blocked':
        case 'auth/web-storage-unsupported':
          return new GeneralError(contextDescription, GeneralErrorCause.unavailable, error)
        case 'auth/account-exists-with-different-credential':
          return new GeneralError(contextDescription, GeneralErrorCause.emailAlreadyInUse, error)
        case 'auth/network-request-failed':
          return new GeneralError(contextDescription, GeneralErrorCause.networkConnection, error)
        case 'auth/too-many-requests':
          return new GeneralError(contextDescription, GeneralErrorCause.forbidden, error)
        case 'auth/internal-error':
          return new GeneralError(contextDescription, GeneralErrorCause.internalServerError, error)
        /// Native (`ASAuthorizationError`)
        case '1000':
          return new GeneralError(contextDescription, GeneralErrorCause.unknown, error)
        case '1001':
          return new GeneralError(contextDescription, GeneralErrorCause.requestCancelled, error)
        case '1002':
          return new GeneralError(contextDescription, GeneralErrorCause.badRequest, error)
        case '1003':
          return new GeneralError(contextDescription, GeneralErrorCause.unimplemented, error)
        case '1004':
          return new GeneralError(contextDescription, GeneralErrorCause.requestCancelled, error)
        default:
          return new GeneralError(contextDescription, cause, error)
      }
    } else {
      return new GeneralError(contextDescription, cause, error)
    }
  },

  fromGeocodingError: (
    error: any,
    contextDescription: string = 'Geocoding error',
    cause?: GeneralErrorCause
  ) => {
    const errorCode = error.code
    if (typeof errorCode === 'number') {
      switch (errorCode) {
        case 1:
          return new GeneralError(contextDescription, GeneralErrorCause.badRequest, error)
        case 2:
          return new GeneralError(contextDescription, GeneralErrorCause.internalServerError, error)
        case 4:
          return new GeneralError(contextDescription, GeneralErrorCause.dataNotFound, error)
        default:
          return new GeneralError(contextDescription, GeneralErrorCause.unknown, error)
      }
    } else {
      return new GeneralError(contextDescription, cause, error)
    }
  },

  fromStripeCheckoutError: (
    error: any,
    contextDescription: string = 'HTTP request error',
    cause?: GeneralErrorCause
  ) => {
    const formattedErrorCode = error.code

    switch (true) {
      case formattedErrorCode === 400:
        return new GeneralError(contextDescription, GeneralErrorCause.invalidData, error)
      case formattedErrorCode >= 400 && formattedErrorCode < 500:
        return new GeneralError(contextDescription, GeneralErrorCause.invalidData, error)
      case formattedErrorCode >= 500:
        return new GeneralError(contextDescription, GeneralErrorCause.internalServerError, error)
      default:
        return new GeneralError(contextDescription, cause, error)
    }
  },

  fromHttpRequestError: (
    error: any,
    contextDescription: string = 'HTTP request error',
    cause?: GeneralErrorCause
  ) => {
    const errorCode = error.code
    if (typeof errorCode === 'string') {
      const formattedErrorCode = Number(errorCode)

      switch (true) {
        case formattedErrorCode >= 400 && formattedErrorCode < 500:
          return new GeneralError(contextDescription, GeneralErrorCause.invalidData, error)
        case formattedErrorCode >= 500:
          return new GeneralError(contextDescription, GeneralErrorCause.internalServerError, error)
        default:
          return new GeneralError(contextDescription, cause, error)
      }
    } else {
      return new GeneralError(contextDescription, cause, error)
    }
  },

  fromFirebaseStorageError: (
    error: any,
    contextDescription: string = 'Firebase Storage error',
    cause?: GeneralErrorCause
  ) => {
    const errorCode = error.code
    if (typeof errorCode === 'string') {
      switch (errorCode) {
        case 'storage/unknown':
          return new GeneralError(contextDescription, GeneralErrorCause.unknown, error)
        case 'storage/object-not-found':
        case 'storage/bucket-not-found':
        case 'storage/project-not-found':
          return new GeneralError(contextDescription, GeneralErrorCause.dataNotFound, error)
        case 'storage/no-default-bucket':
          return new GeneralError(contextDescription, GeneralErrorCause.internalServerError, error)
        case 'storage/quota-exceeded':
          return new GeneralError(contextDescription, GeneralErrorCause.forbidden, error)
        case 'storage/unauthenticated':
          return new GeneralError(contextDescription, GeneralErrorCause.forbidden, error)
        case 'storage/unauthorized':
          return new GeneralError(contextDescription, GeneralErrorCause.forbidden, error)
        case 'storage/canceled':
          return new GeneralError(contextDescription, GeneralErrorCause.requestCancelled, error)
        case 'storage/retry-limit-exceeded':
        case 'storage/server-file-wrong-size':
        case 'storage/cannot-slice-blob':
        case 'storage/invalid-checksum':
        case 'storage/invalid-event-name':
        case 'storage/invalid-url':
        case 'storage/invalid-argument':
          return new GeneralError(contextDescription, GeneralErrorCause.badRequest, error)
        default:
          return new GeneralError(contextDescription, cause, error)
      }
    } else {
      return new GeneralError(contextDescription, cause, error)
    }
  },

  fromBackendServerError: (error: AxiosError<any>) => {
    let cause

    if (error.code === 'ERR_NETWORK') {
      cause = GeneralErrorCause.networkConnection
    } else if (error.response) {
      switch (error.response.status) {
        case 400:
          cause = GeneralErrorCause.badRequest
          break
        case 401:
          cause = GeneralErrorCause.unauthorized
          break
        case 403:
          cause = GeneralErrorCause.forbidden
          break
        case 404:
          cause = GeneralErrorCause.dataNotFound
          break
        case 409:
          cause = GeneralErrorCause.conflict
          break
        case 500:
          cause = GeneralErrorCause.internalServerError
          break
        default:
          cause = GeneralErrorCause.unknown
          break
      }
    }
    return new GeneralError(error.message, cause || GeneralErrorCause.internalServerError, error)
  }
}

export default generalErrorConverter
